import React, { useState } from "react";
import {
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  Slide,
  FormControl,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchBookmarkGroups } from "../store/Bookmarks/bookmarksActions";
import { NotepadText, Folder, FolderPlus } from "lucide-react";
import {
  createBookmarkGroupApi,
  addCaseToGroupApi,
  addStatuteToGroupApi,
} from "../services/api/bookmarksApi";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LabelWithIcon = ({ icon: Icon, label }) => (
  <Typography
    sx={{
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#1F2937",
      mb: 0.5,
      ml: 0.5,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    {Icon && <Icon size="18px" color="#003D78" />}
    {label}
  </Typography>
);

const BookmarkDialog = ({
  open,
  onClose,
  // Case bookmark props
  selectedCase,
  setBookmarkedCaseIds,
  // Statute bookmark props
  selectedStatute,
  setBookmarkedStatuteIds,
  type, // statute subtype
  // Shared
  selectedGroup,
  setSelectedGroup,
}) => {
  const isStatute = Boolean(selectedStatute);
  const isCMSDocBookmark = !isStatute && Boolean(selectedCase?.documentId);

  const dispatch = useDispatch();
  const { groups } = useSelector((state) => state.bookmarkGroups);
  const { userInfo } = useSelector((state) => state.userLogin);

  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false);
  const [bookmarkGroupName, setBookmarkGroupName] = useState("");
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [isAddBookmarkLoading, setIsAddBookmarkLoading] = useState(false);

  const handleClose = () => {
    onClose();
    setSelectedGroup("");
    setNote("");
    setNoteError("");
    setIsCreatingNewGroup(false);
  };

  const handleNoteChange = (value) => {
    if (value.length <= 2000) {
      setNote(value);
      setNoteError("");
    } else {
      setNoteError("You've reached the 2,000-character limit.");
    }
  };

  const handleCreateGroup = async () => {
    if (!bookmarkGroupName) return;
    setIsAddBookmarkLoading(true);

    try {
      const userId = userInfo._id;
      let payload;

      if (isStatute) {
        payload = {
          userId,
          groupName: bookmarkGroupName,
          statuteId: selectedStatute?.Bookmark_ID ?? selectedStatute?.id,
          type: type,
          statuteName:
            selectedStatute?.Act_Ordinance || selectedStatute?.Statute_Name,
          statuteNote: note,
        };
      } else {
        payload = {
          userId,
          groupName: bookmarkGroupName,
          caseId: selectedCase?.Bookmark_ID ?? selectedCase?.id,
          caseNote: note,
        };
      }

      const response = await createBookmarkGroupApi(payload);
      const data = response?.data;

      if (isStatute) {
        if (response.status) {
          toast.success(`Saved to ${bookmarkGroupName}`);
          setBookmarkedStatuteIds(
            (prevIds) => new Set([...prevIds, selectedStatute?.Bookmark_ID]),
          );
          setIsAddBookmarkLoading(false);
          onClose();
          setBookmarkGroupName("");
          setNote("");
          setIsCreatingNewGroup(false);
          dispatch(fetchBookmarkGroups(userInfo._id));
        } else {
          throw new Error(data?.message || "Failed to create bookmark group.");
        }
      } else {
        if (data?.groupId) {
          toast.success(
            `Bookmark group "${bookmarkGroupName}" created successfully.`,
          );
          setBookmarkedCaseIds(
            (prevIds) => new Set([...prevIds, selectedCase?.Bookmark_ID]),
          );
          setIsAddBookmarkLoading(false);
          setIsCreatingNewGroup(false);
          dispatch(fetchBookmarkGroups(userInfo._id));

          if (!isCMSDocBookmark) {
            onClose();
            setBookmarkGroupName("");
            setNote("");
          } else {
            setBookmarkGroupName(bookmarkGroupName);
            setSelectedGroup(data?.groupId);
          }
        } else {
          throw new Error(data?.message || "Failed to create bookmark group.");
        }
      }
    } catch (error) {
      setIsAddBookmarkLoading(false);
      if (
        !isStatute &&
        error.message.includes("Bookmark group created successfully")
      ) {
        toast.success(error.message);
        return;
      }
      console.error("Error creating bookmark group:", error);
      toast.error(error.message);
    }
  };

  const handleAddToGroup = async () => {
    if (!selectedGroup) return;
    setIsAddBookmarkLoading(true);

    try {
      const userId = userInfo._id;
      let response;

      if (isStatute) {
        response = await addStatuteToGroupApi({
          userId,
          groupId: selectedGroup,
          statuteId: selectedStatute?.Bookmark_ID ?? selectedStatute?.id,
          type: type ? type : undefined,
          statuteName:
            selectedStatute?.Act_Ordinance || selectedStatute?.Statute_Name,
          statuteNote: note,
        });
      } else {
        const payload = {
          userId,
          note,
          groupId: selectedGroup,
          caseId: isCMSDocBookmark ? selectedCase?.caseId : (selectedCase?.Bookmark_ID ?? selectedCase?.id),
          documentId: selectedCase?.documentId,
        }
        // console.log("Add to group payload:", payload);

        response = await addCaseToGroupApi(payload);
      }

      const data = response?.data;

      if (response.status === 200) {
        toast.success(
          `Saved to ${
            groups?.find((group) => group._id === selectedGroup)?.groupName
          } bookmark`,
        );
        if (isStatute) {
          setBookmarkedStatuteIds(
            (prevIds) => new Set([...prevIds, selectedStatute?.Bookmark_ID]),
          );
        } else {
          setBookmarkedCaseIds(
            (prevIds) => new Set([...prevIds, selectedCase?.Bookmark_ID]),
          );
        }
        setIsAddBookmarkLoading(false);
        dispatch(fetchBookmarkGroups(userInfo._id));
        onClose();
        setNote("");
      } else {
        throw new Error(
          data?.message ||
            `Failed to add ${isStatute ? "statute" : "case"} to group.`,
        );
      }
    } catch (error) {
      setIsAddBookmarkLoading(false);
      toast.error(error.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: { xs: "12px", sm: "16px" },
          boxShadow: "0 8px 32px 0 rgba(60, 80, 177, 0.13)",
          background: "#ffffff",
          margin: { xs: 2, sm: 0 },
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          background: "rgba(30,41,59,0.25)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Box
        sx={{
          minHeight: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          background: "linear-gradient(135deg, #34D399 0%, #059669 100%)",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BookmarkBorderIcon sx={{ color: "#ffffff", fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#ffffff",
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
            }}
          >
            Save Bookmark
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{
            color: "#0f0303ff",
            background: "rgba(255, 255, 255, 0.15)",
            "&:hover": { background: "rgba(255, 255, 255, 0.25)" },
            p: 1,
            borderRadius: "50%",
            zIndex: 1200,
          }}
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: 400 },
          boxSizing: "border-box",
          p: { xs: 2, lg: 3 },
          bgcolor: "white",
        }}
      >
        <LabelWithIcon icon={Folder} label="Group" />
        <FormControl fullWidth variant="outlined" sx={{ mb: "6px" }}>
          <TextField
            variant="outlined"
            select
            fullWidth
            size="small"
            value={selectedGroup}
            onChange={(e) => {
              setSelectedGroup(e.target.value);
              setIsCreatingNewGroup(e.target.value === "new");
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: { sx: { maxHeight: 150 } },
              },
            }}
            sx={{
              mb: { xs: 1, lg: 2 },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          >
            <MenuItem
              value="new"
              sx={{ color: "#303f9f", fontSize: "12px", fontWeight: "bold" }}
            >
              Create New Group
            </MenuItem>
            {groups
              ?.sort((a, b) =>
                a.groupName === "Bookmarks"
                  ? -1
                  : b.groupName === "Bookmarks"
                    ? 1
                    : 0,
              )
              ?.map((group) => (
                <MenuItem
                  key={group._id}
                  value={group._id}
                  sx={{ fontSize: "12px" }}
                >
                  {group.groupName}
                </MenuItem>
              ))}
          </TextField>
        </FormControl>

        {isCreatingNewGroup && (
          <>
            <LabelWithIcon icon={FolderPlus} label="New Group" />
            <FormControl fullWidth variant="outlined" sx={{ mb: "6px" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter group name"
                variant="outlined"
                value={bookmarkGroupName}
                onChange={(e) => setBookmarkGroupName(e.target.value)}
                sx={{
                  mb: { xs: 1, lg: 2 },
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />
            </FormControl>
          </>
        )}

        <LabelWithIcon icon={NotepadText} label="Note" />
        <FormControl fullWidth variant="outlined" sx={{ mb: "6px" }}>
          <TextField
            placeholder="Add a note (optional)"
            fullWidth
            multiline
            rows={4}
            value={note}
            onChange={(e) => handleNoteChange(e.target.value)}
            error={Boolean(noteError)}
            helperText={noteError || `${note.length}/2000`}
            FormHelperTextProps={{
              sx: { textAlign: noteError ? "left" : "right", m: 0 },
            }}
            sx={{
              mb: { xs: 1, lg: 2 },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            }}
          />
        </FormControl>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: "8px",
            mt: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            color="error"
            sx={{
              fontSize: { xs: "13px", lg: "14px" },
              borderRadius: "24px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              isAddBookmarkLoading && (
                <CircularProgress size={16} sx={{ color: "white" }} />
              )
            }
            onClick={() => {
              isCreatingNewGroup ? handleCreateGroup() : handleAddToGroup();
            }}
            disabled={
              isCreatingNewGroup
                ? !bookmarkGroupName
                : !selectedGroup || Boolean(noteError)
            }
            sx={{
              fontSize: { xs: "13px", lg: "14px" },
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#003D78",
              "&:hover": { backgroundColor: "#002b54" },
            }}
          >
            {isCreatingNewGroup ? "Create Bookmark Group" : "Save to Bookmark"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default BookmarkDialog;
